import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

export type AppRole = "admin" | "assistant";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  role: AppRole | null;
  loading: boolean;
  signUp: (email: string, password: string, displayName: string) => Promise<{ error: string | null }>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = role === "admin" || role === "assistant";

  const fetchRole = async (userId: string): Promise<AppRole | null> => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .in("role", ["admin", "assistant"])
      .order("role", { ascending: true }) // admin sorts before assistant
      .limit(1)
      .maybeSingle();

    return (data?.role as AppRole | undefined) ?? null;
  };


  useEffect(() => {
    let mounted = true;

    // Initial session load
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const r = await fetchRole(session.user.id);
        if (mounted) setRole(r);
      }
      if (mounted) setLoading(false);
    });

    // Listen for auth changes — don't block the callback
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchRole(session.user.id).then((r) => {
            if (mounted) setRole(r);
          });
        } else {
          setRole(null);
        }
        setLoading(false);

      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, displayName: string) => {
    const redirectTo = `${window.location.origin}/account`;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName },
        emailRedirectTo: redirectTo,
      },
    });
    if (!error && data.user) {
      // Fire-and-forget welcome email — don't block signup on email send
      supabase.functions
        .invoke("send-transactional-email", {
          body: {
            templateName: "welcome",
            recipientEmail: email,
            idempotencyKey: `welcome-${data.user.id}`,
            templateData: { customerName: displayName },
          },
        })
        .catch((err) => console.error("[welcome email] failed:", err));
    }
    return { error: error?.message ?? null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  };

  const signOut = async () => {
    console.log("[useAuth] signOut called, clearing state");
    setUser(null);
    setSession(null);
    setRole(null);
    setLoading(false);
    try {
      await supabase.auth.signOut();
      console.log("[useAuth] signOut completed");
    } catch (err) {
      console.error("[useAuth] signOut error:", err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, isAdmin, role, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
